// 라벨 (key, value) 를 사람이 읽는 표기로 바꾸는 단일 정의 파일.
//
// 카드, 목록 테이블, 리뷰 큐가 모두 이 파일로 칩 문구를 만든다. 같은 라벨이 화면마다 다르게
// 표시되지 않도록 표기 규칙은 이 파일 한 곳에만 둔다.
//
// 소유 경계: 이 파일은 **어떻게 표시할지**만 정한다. 한국어 문구의 SoT 는 `labelGuide`(값)와
// `reservedLabelConfig`(key)다. 어느 값이 존재하는지는 서버 계약이 정한다. 문구가 없는 값은
// 원본을 그대로 반환한다. 그래서 계약에 새 값이 먼저 들어와도 깨지지 않는다.

import { QUALITY_KEY, RESERVED_KEYS } from "@/api";
import { episodeQualityTagLabel } from "./episodeQualityTags";
import { reservedValueLabel } from "./labelGuide";
import { formatReservedValue, reservedKeyLabel } from "./reservedLabelConfig";

// 라벨 값의 표시 문구. 위에서부터 먼저 맞는 규칙을 쓴다.
export function labelValueText(key: string, value: string): string {
  // quality 값은 에피소드 리뷰 칩과 같은 문구를 쓴다.
  if (key === QUALITY_KEY) {
    return episodeQualityTagLabel(value);
  }
  const korean = reservedValueLabel(key, value);
  if (korean !== null) {
    return korean;
  }
  // open 정책 reserved key(task)는 허용값 목록이 없어 한국어 문구를 둘 수 없다. snake_case 를
  // 사람이 읽는 형태로 바꾸는 것까지만 한다(pick_place → Pick place).
  if (RESERVED_KEYS.includes(key)) {
    return formatReservedValue(value);
  }
  // generic 라벨 값은 사용자가 직접 지은 문자열이라 대소문자·구분자를 건드리지 않는다.
  return value;
}

// 라벨 칩 한 줄의 표시 문구. 예: "출처: 알바". reserved 가 아닌 key 는 `reservedKeyLabel` 이
// 원본을 반환하므로 "robot: ur5" 처럼 표시된다.
export function labelDisplayText(key: string, value: string): string {
  return `${reservedKeyLabel(key)}: ${labelValueText(key, value)}`;
}

// 라벨을 표시 순서로 정렬한다. 필수 라벨(environment → source → task)이 앞, 나머지가 뒤다.
// 카드는 앞에서 칩 3개만 표시한다. 정렬하지 않으면 어떤 필수 라벨이 표시될지가 서버 배열
// 순서에 따라 임의로 정해진다. 순서 상수를 새로 두지 않고 `RESERVED_KEYS`(reserved key 의 SoT)의
// 선언 순서를 그대로 쓴다. 순위가 같으면 서버가 준 순서를 유지한다(stable sort).
export function sortLabelsForDisplay<T extends { key: string }>(labels: readonly T[]): T[] {
  const rank = (key: string): number => {
    const index = RESERVED_KEYS.indexOf(key);
    return index < 0 ? RESERVED_KEYS.length : index;
  };
  return [...labels].sort((left, right) => rank(left.key) - rank(right.key));
}
